import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const V_MID = "#EDE9FE"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"
const SUCCESS = "#059669"
const SUCCESS_BG = "#ECFDF5"
const SUCCESS_MID = "#D1FAE5"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"

const DAYS = {
  es: ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"],
  en: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
}

const FORMAT_COLORS = {
  "Post":      { bg:"#F0FDF4", color:"#16A34A" },
  "Carrusel":  { bg:"#EFF6FF", color:"#2563EB" },
  "Carousel":  { bg:"#EFF6FF", color:"#2563EB" },
  "Reel":      { bg:"#FDF4FF", color:"#9333EA" },
  "Historia":  { bg:"#FFF7ED", color:"#EA580C" },
  "Story":     { bg:"#FFF7ED", color:"#EA580C" },
  "Video":     { bg:"#FEF2F2", color:"#DC2626" },
}

const IMAGE_STYLES_GRADIENTS = {
  "Editorial":    "linear-gradient(135deg,#F5F0E8 0%,#E8DDD0 100%)",
  "Lifestyle":    "linear-gradient(135deg,#FFF8F0 0%,#FFD9C0 100%)",
  "Minimalista":  "linear-gradient(135deg,#F0F0F5 0%,#E0E0F0 100%)",
  "Ilustración":  "linear-gradient(135deg,#FFF0F8 0%,#FFD6F0 100%)",
  "Cinematic":    "linear-gradient(135deg,#1A1A2E 0%,#2D2D4E 100%)",
  "Bold":         "linear-gradient(135deg,#3D1A6E 0%,#7C3AED 100%)",
  "Animado 3D":        "linear-gradient(135deg,#FFF0F8 0%,#E8D5FF 100%)",
}

// Simulated weeks — in production this comes from Supabase
const WEEKS_DATA = {
  es: [
    {
      id: "w3", label: "Semana actual", dates: "28 Abr – 4 May 2025",
      focus: "Posicionamiento", current: true,
      estrategia: "Esta semana construimos presencia de marca mostrando quién eres y qué te hace diferente. Cada pieza habla directamente a emprendedoras que buscan claridad.",
      estilo_imagen: "Editorial",
      img_concepto: "Mujer en estudio minimalista, luz natural suave, escritorio ordenado, paleta beige y blanco, atmósfera serena y profesional.",
      imagenes: [
        { id:"i1", generada: true, concepto:"Estudio minimalista con luz natural", dia: 0 },
        { id:"i2", generada: false, concepto:"Escritorio de trabajo ordenado", dia: 3 },
        { id:"i3", generada: false, concepto:"Espacio creativo con plantas", dia: 6 },
      ],
      dias: [
        { hook:"¿Tu marca dice todo lo que eres, o solo lo que crees que la gente quiere escuchar? Hoy hablamos de autenticidad real.", cta:"Cuéntame: ¿qué parte de tu marca aún no has mostrado?", formato:"Post", img_id:"i1" },
        { hook:"3 señales de que tu marca necesita una estrategia urgente — y no es lo que crees.", cta:"Agenda tu sesión de claridad de marca este mes.", formato:"Carrusel", img_id:null },
        { hook:"La diferencia entre una marca que se recuerda y una que se olvida no es el diseño. Es esto.", cta:"Descubre tu diferenciador en nuestra sesión gratuita.", formato:"Reel", img_id:null },
        { hook:"Si tuvieras que describir tu marca en una sola oración, ¿lo podrías hacer? La mayoría no puede.", cta:"Empieza hoy con nuestra metodología de 90 días.", formato:"Historia", img_id:"i2" },
        { hook:"El posicionamiento no es lo que tú dices de ti. Es lo que tu cliente siente cuando te ve.", cta:"¿Lista para que tu marca hable por sí sola?", formato:"Video", img_id:null },
        { hook:"Semana 1 de trabajar tu posicionamiento: lo que nadie te dice que va a pasar.", cta:"Únete a nuestro próximo grupo de coaching.", formato:"Carrusel", img_id:null },
        { hook:"Tu marca ya existe en la mente de tu cliente. La pregunta es: ¿existe como tú quieres?", cta:"Hablemos esta semana — agenda aquí.", formato:"Post", img_id:"i3" },
      ]
    },
    {
      id: "w2", label: "Semana pasada", dates: "21 – 27 Abr 2025",
      focus: "Generar ventas", current: false,
      estrategia: "Enfocamos el contenido en mostrar el valor real del servicio y derribar objeciones de precio. Cada pieza empujó hacia una acción de venta concreta.",
      estilo_imagen: "Lifestyle",
      img_concepto: "Mujer emprendedora trabajando con confianza, espacio cálido y acogedor, luz dorada.",
      imagenes: [
        { id:"i4", generada: true, concepto:"Emprendedora con confianza", dia: 0 },
        { id:"i5", generada: true, concepto:"Espacio de trabajo cálido", dia: 2 },
        { id:"i6", generada: true, concepto:"Celebración de logros", dia: 6 },
      ],
      dias: [
        { hook:"Una emprendedora aumentó sus ventas 3x en 60 días sin cambiar su producto. Solo cambió cómo lo presentaba.", cta:"Agenda tu sesión estratégica ahora.", formato:"Reel", img_id:"i4" },
        { hook:"El precio no es el problema. El problema es que tu cliente no ve el valor todavía.", cta:"Descubre cómo comunicar tu valor real.", formato:"Carrusel", img_id:null },
        { hook:"Vender sin sentirte vendiendo — así se ve una marca con estrategia real.", cta:"Empieza el mes con claridad total.", formato:"Post", img_id:"i5" },
        { hook:"¿Cuánto vale realmente lo que ofreces? Probablemente más de lo que estás cobrando.", cta:"Hablemos de tu estrategia de precios.", formato:"Historia", img_id:null },
        { hook:"El error más común al lanzar: hablarle a todos y no conectar con nadie.", cta:"Agenda tu sesión de audiencia ideal.", formato:"Video", img_id:null },
        { hook:"Cerrar una venta no es convencer. Es encontrar a quien ya estaba buscando lo que tú tienes.", cta:"Descubre tu cliente ideal este mes.", formato:"Carrusel", img_id:null },
        { hook:"Venta cerrada. Cliente feliz. Marca que crece. Así se ve el trabajo bien hecho.", cta:"¿Quieres esto para tu negocio?", formato:"Post", img_id:"i6" },
      ]
    },
  ],
  en: [
    {
      id: "w3", label: "Current week", dates: "Apr 28 – May 4, 2025",
      focus: "Positioning", current: true,
      estrategia: "This week we build brand presence by showing who you are and what makes you different. Every piece speaks directly to entrepreneurs looking for clarity.",
      estilo_imagen: "Editorial",
      img_concepto: "Woman in minimalist studio, soft natural light, tidy desk, beige and white palette, serene and professional atmosphere.",
      imagenes: [
        { id:"i1", generada: true, concepto:"Minimalist studio with natural light", dia: 0 },
        { id:"i2", generada: false, concepto:"Organized work desk", dia: 3 },
        { id:"i3", generada: false, concepto:"Creative space with plants", dia: 6 },
      ],
      dias: [
        { hook:"Does your brand say everything you are, or just what you think people want to hear? Today we talk about real authenticity.", cta:"Tell me: what part of your brand haven't you shown yet?", formato:"Post", img_id:"i1" },
        { hook:"3 signs your brand urgently needs a strategy — and it's not what you think.", cta:"Book your brand clarity session this month.", formato:"Carousel", img_id:null },
        { hook:"The difference between a brand that's remembered and one that's forgotten isn't the design. It's this.", cta:"Discover your differentiator in our free session.", formato:"Reel", img_id:null },
        { hook:"If you had to describe your brand in one sentence, could you do it? Most people can't.", cta:"Start today with our 90-day methodology.", formato:"Story", img_id:"i2" },
        { hook:"Positioning isn't what you say about yourself. It's what your client feels when they see you.", cta:"Ready for your brand to speak for itself?", formato:"Video", img_id:null },
        { hook:"Week 1 of working on your positioning: what nobody tells you is going to happen.", cta:"Join our next coaching group.", formato:"Carousel", img_id:null },
        { hook:"Your brand already exists in your client's mind. The question is: does it exist the way you want?", cta:"Let's talk this week — book here.", formato:"Post", img_id:"i3" },
      ]
    },
  ]
}

// Simulated published state
const INITIAL_PUB = {
  w3: [false,false,false,false,false,false,false],
  w2: [true,true,true,false,true,false,true],
}

function ImageMockup({ concepto, estilo, nombre, small = false }) {
  const gradient = IMAGE_STYLES_GRADIENTS[estilo] || `linear-gradient(135deg,${V} 0%,#4C1D95 100%)`
  const isDark = estilo === "Cinematic" || estilo === "Bold"
  const textColor = isDark ? WHITE : "#1A1A18"
  const subColor = isDark ? "rgba(255,255,255,.6)" : MUTED
  return (
    <div style={{ background: gradient, borderRadius: small ? 8 : 12, padding: small ? ".6rem" : "1.25rem", textAlign: "center", position: "relative", overflow: "hidden", height: small ? 72 : 140 }}>
      <div style={{ position: "absolute", inset: 0, opacity: .08, backgroundImage: "radial-gradient(circle at 20% 80%,#fff 0%,transparent 50%)" }} />
      <div style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: small ? 2 : 5 }}>
        <div style={{ fontFamily: H, fontSize: small ? 11 : 18, fontWeight: 800, color: textColor, letterSpacing: "-.01em", lineHeight: 1 }}>{nombre}</div>
        {!small && <div style={{ width: 24, height: 1.5, background: isDark ? "rgba(255,255,255,.4)" : "rgba(0,0,0,.15)", borderRadius: 1 }} />}
        {!small && <div style={{ fontSize: 10, color: subColor, fontStyle: "italic", maxWidth: 200, lineHeight: 1.4, fontFamily: B }}>{concepto}</div>}
        <div style={{ position: "absolute", bottom: small ? 4 : 8, right: small ? 6 : 10, fontSize: 9, color: isDark ? "rgba(255,255,255,.5)" : "rgba(0,0,0,.3)", fontFamily: B, letterSpacing: ".06em" }}>IA</div>
      </div>
    </div>
  )
}

export default function KorivaArchive() {
  const [lang, setLang] = useState("es")
  const [activeWeek, setActiveWeek] = useState("w3")
  const [published, setPublished] = useState(INITIAL_PUB)
  const [copied, setCopied] = useState(null)
  const [expandedDay, setExpandedDay] = useState(null)
  const [viewImg, setViewImg] = useState(null)
  const [editingDay, setEditingDay] = useState(null)
  const [edits, setEdits] = useState({})

  const es = lang === "es"
  const navigate = useNavigate()
  const [realWeeks, setRealWeeks] = useState(null)
  const [loadingWeeks, setLoadingWeeks] = useState(true)
  const [nombreProyecto, setNombreProyecto] = useState("")

  useEffect(() => {
    async function cargarSemanas() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) { setLoadingWeeks(false); return }
        // Leer el idioma guardado del usuario
        const { data: userData } = await supabase
          .from("users").select("lang").eq("id", session.user.id).maybeSingle()
        if (userData && userData.lang) setLang(userData.lang)
        const { data: proyectos } = await supabase
          .from("projects").select("id, nombre").eq("user_id", session.user.id).eq("activo", true).limit(1)
        if (!proyectos || proyectos.length === 0) { setLoadingWeeks(false); return }
        setNombreProyecto(proyectos[0].nombre || "")
        const { data: semanas } = await supabase
          .from("weeks").select("*").eq("project_id", proyectos[0].id).order("created_at", { ascending: false })
        if (semanas) {
          const traducidas = semanas.map((s, idx) => ({
            id: s.id,
            label: idx === 0 ? (es ? "Semana actual" : "Current week") : (es ? "Semana pasada" : "Past week"),
            dates: (() => {
              const creacion = new Date(s.created_at)
              const dia = creacion.getDay() // 0=domingo, 1=lunes...
              const diffLunes = dia === 0 ? 6 : dia - 1
              const lunes = new Date(creacion)
              lunes.setDate(creacion.getDate() - diffLunes)
              const domingo = new Date(lunes)
              domingo.setDate(lunes.getDate() + 6)
              const opcionesInicio = { day:"numeric", month:"short" }
              const opcionesFin = { day:"numeric", month:"short", year:"numeric" }
              const fechaInicio = lunes.toLocaleDateString(es ? "es-ES" : "en-US", opcionesInicio)
              const fechaFin = domingo.toLocaleDateString(es ? "es-ES" : "en-US", opcionesFin)
              return `${fechaInicio} - ${fechaFin}`
            })(),
            focus: s.enfoque || "",
            current: idx === 0,
            estrategia: s.resumen_semana || "",
            estilo_imagen: "",
            img_concepto: "",
            imagenes: (s.dias || []).map((d, di) => {
              const url = d.img_url || (d.img_urls && d.img_urls[0]) || null
              return { id: "d" + di, generada: !!url, url: url, urls: d.img_urls || null, concepto: d.img_idea || d.hook || "", dia: di }
            }).filter(im => im.generada),
            dias: (s.dias || []).map((d, di) => ({
              hook: d.hook || "",
              cta: d.cta || "",
              formato: d.formato || "Post",
              img_url: d.img_url || null,
              img_urls: d.img_urls || null,
              img_id: (d.img_url || (d.img_urls && d.img_urls[0])) ? "d" + di : null,
              publicado: d.publicado || false,
            }))
          }))
          setRealWeeks(traducidas)
          if (traducidas.length > 0) setActiveWeek(traducidas[0].id)
          // cargar el estado publicado de cada semana
          const pubInicial = {}
          traducidas.forEach(w => {
            pubInicial[w.id] = w.dias.map(d => d.publicado || false)
          })
          setPublished(pubInicial)
        }
      } catch (e) {
        console.error("Error cargando semanas en archive:", e)
      }
      setLoadingWeeks(false)
    }
    cargarSemanas()
  }, [es])

  const weeks = realWeeks && realWeeks.length > 0 ? realWeeks : (WEEKS_DATA[lang] || WEEKS_DATA.es)
  const days = DAYS[lang]
  const week = weeks.find(w => w.id === activeWeek) || weeks[0]
  const weekPub = published[activeWeek] || Array(7).fill(false)
  const pubCount = weekPub.filter(Boolean).length
  const allDone = pubCount === 7
  const progress = (pubCount / 7) * 100
  const generatedImgs = week.imagenes.filter(i => i.generada)

  const T = {
    es: {
      arch: "Archivo semanal", weeks: "Semanas",
      focus: "Enfoque", strategy: "Estrategia de la semana",
      images: "Imágenes guardadas", imgGenerated: "guardada", imgPending: "sin generar",
      genImgBtn: "Generar imagen", viewBtn: "Ver", downloadBtn: "Descargar",
      hook: "Hook", cta: "CTA",
      published: "Publicado", pending: "Pendiente",
      copy: "Copiar", copied: "¡Copiado!",
      progress: "Publicados", allDone: "¡Semana completa!",
      videoNote: "Usa este hook como tu primera línea en cámara. Tú grabas el video.",
      thisWeek: "Esta semana", newWeek: "Nueva semana →",
      imgFor: "Imagen para el",
      noImg: "Sin imagen asignada",
      imgCount: "imagen guardada", imgCountP: "imágenes guardadas",
      imgStorage: "Guardadas en tu biblioteca",
    },
    en: {
      arch: "Weekly archive", weeks: "Weeks",
      focus: "Focus", strategy: "Week strategy",
      images: "Saved images", imgGenerated: "saved", imgPending: "not generated",
      genImgBtn: "Generate image", viewBtn: "View", downloadBtn: "Download",
      hook: "Hook", cta: "CTA",
      published: "Published", pending: "Pending",
      copy: "Copy", copied: "Copied!",
      progress: "Published", allDone: "Week complete!",
      videoNote: "Use this hook as your first line on camera. You record the video.",
      thisWeek: "This week", newWeek: "New week →",
      imgFor: "Image for",
      noImg: "No image assigned",
      imgCount: "saved image", imgCountP: "saved images",
      imgStorage: "Saved in your library",
    }
  }
  const t = T[lang]

  async function togglePub(weekId, dayIdx) {
    // Calcular el nuevo array ANTES (no depender de setPublished)
    const actual = published[weekId] || Array(7).fill(false)
    const nuevoArr = [...actual]
    nuevoArr[dayIdx] = !nuevoArr[dayIdx]
    setPublished(prev => ({ ...prev, [weekId]: nuevoArr }))
    // Guardar en Supabase dentro de dias (solo si es una semana real)
    if (realWeeks) {
      const semana = realWeeks.find(w => w.id === weekId)
      if (semana) {
        const diasActualizados = semana.dias.map((d, di) => ({ ...d, publicado: nuevoArr[di] }))
        const { error } = await supabase.from("weeks").update({ dias: diasActualizados }).eq("id", weekId)
        if (error) console.error("Error guardando publicado:", error)
      }
    }
  }

  function copy(text, key) {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopied(key); setTimeout(() => setCopied(null), 2000)
  }

  async function guardarEdicion(weekId, dayIdx) {
    const key = `${weekId}-${dayIdx}`
    const ed = edits[key]
    setEditingDay(null)
    if (!ed || !realWeeks) return
    const semana = realWeeks.find(w => w.id === weekId)
    if (!semana) return
    const diasActualizados = semana.dias.map((d, di) => {
      if (di !== dayIdx) return d
      return {
        ...d,
        hook: ed.hook ?? d.hook,
        cta: ed.cta ?? d.cta,
        img_idea: ed.concepto ?? d.img_idea,
      }
    })
    // actualizar en memoria para que se vea el cambio
    setRealWeeks(prev => prev.map(w => w.id === weekId ? { ...w, dias: w.dias.map((d,di)=> di===dayIdx ? { ...d, hook: ed.hook ?? d.hook, cta: ed.cta ?? d.cta } : d) } : w))
    const { error } = await supabase.from("weeks").update({ dias: diasActualizados }).eq("id", weekId)
    if (error) console.error("Error guardando edición:", error)
  }

  const s = {
    page: { minHeight: "100vh", background: WHITE, fontFamily: B, color: TEXT },
    header: { borderBottom: `1px solid ${BORDER}`, padding: ".85rem 2rem", display: "flex", alignItems: "center", justifyContent: "space-between" },
    logo: { fontFamily: H, fontSize: 17, fontWeight: 800, color: TEXT, letterSpacing: "-.02em" },
    logoV: { color: V },
    headerRight: { display: "flex", alignItems: "center", gap: "1rem" },
    brandChip: { display: "flex", alignItems: "center", gap: 6, background: OFF, border: `1px solid ${BORDER}`, borderRadius: 20, padding: "4px 12px" },
    brandDot: { width: 6, height: 6, background: SUCCESS, borderRadius: "50%" },
    brandName: { fontSize: 12, fontWeight: 600, color: TEXT },
    langRow: { display: "flex", gap: 3, background: OFF, borderRadius: 8, padding: 3, border: `1px solid ${BORDER}` },
    langBtn: (a) => ({ background: a ? V : "transparent", color: a ? WHITE : MUTED, border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: B, transition: "all .18s" }),
    layout: { display: "grid", gridTemplateColumns: "200px 1fr", minHeight: "calc(100vh - 54px)" },
    sidebar: { borderRight: `1px solid ${BORDER}`, padding: "1.5rem 1rem", background: OFF },
    sideLabel: { fontSize: 10, fontWeight: 700, letterSpacing: ".12em", color: MUTED, textTransform: "uppercase", marginBottom: "1rem", fontFamily: B },
    weekItem: (a) => ({ border: a ? `1.5px solid ${V}` : `1.5px solid transparent`, background: a ? WHITE : "transparent", borderRadius: 10, padding: ".7rem .85rem", cursor: "pointer", marginBottom: 6, transition: "all .18s", position: "relative", overflow: "hidden" }),
    weekLine: (a) => ({ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: a ? V : "transparent" }),
    weekLabel: (a) => ({ fontFamily: H, fontSize: 12, fontWeight: 700, color: a ? V : TEXT, marginBottom: 1, letterSpacing: "-.01em" }),
    weekDates: { fontSize: 11, color: MUTED, marginBottom: 4 },
    weekProg: { display: "flex", alignItems: "center", gap: 5 },
    weekProgBar: { flex: 1, height: 3, background: BORDER, borderRadius: 2, overflow: "hidden" },
    weekProgFill: (pct, cur) => ({ height: "100%", width: `${pct}%`, background: cur ? V : SUCCESS, borderRadius: 2 }),
    weekProgTxt: { fontSize: 10, color: MUTED },
    curBadge: { display: "inline-block", fontSize: 9, fontWeight: 700, color: V, background: V_SOFT, border: `1px solid ${V_MID}`, borderRadius: 10, padding: "1px 7px", marginBottom: 3, textTransform: "uppercase", fontFamily: B },
    newWeekBtn: { width: "100%", padding: "9px", background: V, color: WHITE, border: "none", borderRadius: 8, fontFamily: H, fontSize: 12, fontWeight: 700, cursor: "pointer", marginTop: "1rem" },
    main: { padding: "1.75rem 2rem", overflowY: "auto" },
    topRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.25rem", gap: "1rem" },
    topLeft: {},
    topLbl: { fontSize: 10, fontWeight: 700, letterSpacing: ".14em", color: V, textTransform: "uppercase", marginBottom: ".3rem", fontFamily: B },
    weekTitle: { fontFamily: H, fontSize: 22, fontWeight: 800, color: TEXT, letterSpacing: "-.03em", marginBottom: 3 },
    weekMeta: { display: "flex", alignItems: "center", gap: 7 },
    weekDatesTxt: { fontSize: 12, color: MUTED },
    focusChip: { display: "inline-flex", alignItems: "center", gap: 4, background: V_SOFT, border: `1px solid ${V_MID}`, borderRadius: 20, padding: "2px 9px", fontSize: 10, color: V, fontWeight: 700, textTransform: "uppercase", fontFamily: B },
    progBox: { background: OFF, border: `1px solid ${BORDER}`, borderRadius: 10, padding: ".75rem 1rem", minWidth: 140, textAlign: "right" },
    progTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 },
    progLbl: { fontSize: 11, color: MUTED },
    progNum: (done) => ({ fontFamily: H, fontSize: 15, fontWeight: 800, color: done ? SUCCESS : V }),
    progBar: { height: 4, background: BORDER, borderRadius: 2, overflow: "hidden" },
    progFill: (done) => ({ height: "100%", width: `${progress}%`, background: done ? SUCCESS : V, borderRadius: 2, transition: "width .4s" }),
    stratBox: { background: V_SOFT, border: `1px solid ${V_MID}`, borderRadius: 10, padding: ".85rem 1rem", marginBottom: "1.25rem", display: "flex", gap: 8, alignItems: "flex-start" },
    stratDot: { width: 7, height: 7, background: V, borderRadius: "50%", flexShrink: 0, marginTop: 5 },
    stratTxt: { fontSize: 13, color: TEXT, lineHeight: 1.7, fontStyle: "italic", fontWeight: 400 },
    imgSection: { marginBottom: "1.25rem" },
    imgSectionTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".65rem" },
    imgSectionLbl: { fontSize: 10, fontWeight: 700, letterSpacing: ".12em", color: MUTED, textTransform: "uppercase", fontFamily: B },
    imgStorageBadge: { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: SUCCESS, fontWeight: 600 },
    imgStorageDot: { width: 6, height: 6, background: SUCCESS, borderRadius: "50%" },
    imgGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(100px,1fr))", gap: 8 },
    imgThumb: (gen) => ({ borderRadius: 8, overflow: "hidden", border: `1.5px solid ${gen ? SUCCESS_MID : BORDER}`, position: "relative", cursor: gen ? "pointer" : "default" }),
    imgPendingOverlay: { position: "absolute", inset: 0, background: "rgba(255,255,255,.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 },
    imgPendingTxt: { fontSize: 9, color: MUTED, fontWeight: 600, textAlign: "center", padding: "0 4px" },
    genImgBtn: { fontSize: 9, fontWeight: 700, color: WHITE, background: V, border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontFamily: B },
    allDoneBanner: { background: SUCCESS_BG, border: `1px solid ${SUCCESS_MID}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: SUCCESS, fontWeight: 700, textAlign: "center", marginBottom: "1rem", fontFamily: H },
    dayCard: (pub, exp) => ({ border: `1.5px solid ${pub ? SUCCESS_MID : (exp ? V_MID : BORDER)}`, background: pub ? SUCCESS_BG : WHITE, borderRadius: 12, marginBottom: 8, overflow: "hidden", transition: "all .2s" }),
    dayHeader: (pub) => ({ padding: ".6rem .9rem", display: "flex", alignItems: "center", justifyContent: "space-between", background: pub ? SUCCESS_BG : OFF, borderBottom: `1px solid ${pub ? SUCCESS_MID : BORDER}`, cursor: "pointer" }),
    dayLeft: { display: "flex", alignItems: "center", gap: 8 },
    checkbox: (pub) => ({ width: 19, height: 19, borderRadius: 5, border: `2px solid ${pub ? SUCCESS : BORDER}`, background: pub ? SUCCESS : WHITE, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all .2s", flexShrink: 0 }),
    checkmark: { fontSize: 10, color: WHITE, fontWeight: 800 },
    dayName: (pub) => ({ fontFamily: H, fontSize: 12, fontWeight: 700, color: pub ? SUCCESS : TEXT }),
    dayStatus: (pub) => ({ fontSize: 9, color: pub ? SUCCESS : MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".07em" }),
    dayRight: { display: "flex", alignItems: "center", gap: 6 },
    fmtBadge: (fmt) => { const c = FORMAT_COLORS[fmt] || { bg:OFF, color:MUTED }; return { background:c.bg, color:c.color, fontSize:9, fontWeight:700, letterSpacing:".08em", padding:"2px 7px", borderRadius:20, textTransform:"uppercase", fontFamily:B } },
    expandIcon: (exp) => ({ fontSize: 12, color: MUTED, transition: "transform .2s", transform: exp ? "rotate(180deg)" : "none" }),
    dayBody: { padding: ".85rem .9rem" },
    dayGrid: { display: "grid", gridTemplateColumns: "1fr auto", gap: "1rem", alignItems: "start" },
    dayContent: {},
    fieldBlock: { marginBottom: ".65rem" },
    miniLbl: (pub) => ({ fontSize: 9, fontWeight: 700, letterSpacing: ".12em", color: pub ? SUCCESS : V, textTransform: "uppercase", marginBottom: ".3rem", fontFamily: B }),
    contentRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 },
    contentTxt: (pub) => ({ fontSize: 13, color: pub ? MUTED : TEXT, lineHeight: 1.65, fontWeight: 400, flex: 1 }),
    ctaTxt: (pub) => ({ fontSize: 13, color: pub ? MUTED : TEXT, lineHeight: 1.65, fontWeight: 500, flex: 1 }),
    copyBtn: (a) => ({ background: a ? V : "transparent", color: a ? WHITE : MUTED, border: `1px solid ${a ? V : BORDER}`, borderRadius: 5, padding: "3px 8px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: B, transition: "all .18s", whiteSpace: "nowrap", flexShrink: 0 }),
    divLine: { height: 1, background: BORDER, margin: ".55rem 0" },
    videoNote: { marginTop: 5, fontSize: 11, color: MUTED, background: OFF, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "4px 9px", fontStyle: "italic" },
    dayImgWrap: { width: 88, flexShrink: 0 },
    dayImgLbl: { fontSize: 9, color: MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4, fontFamily: B },
    dayGenBtn: { width: "100%", padding: "5px 0", background: V_SOFT, color: V, border: `1px solid ${V_MID}`, borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: B, marginTop: 5, transition: "all .18s" },
    dayViewBtn: { width: "100%", padding: "5px 0", background: SUCCESS_BG, color: SUCCESS, border: `1px solid ${SUCCESS_MID}`, borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: B, marginTop: 5 },
    modal: { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "2rem" },
    modalBox: { background: WHITE, borderRadius: 16, padding: "1.5rem", maxWidth: 400, width: "100%", maxHeight: "85vh", overflowY: "auto" },
    modalTitle: { fontFamily: H, fontSize: 18, fontWeight: 800, color: TEXT, marginBottom: ".5rem", letterSpacing: "-.02em" },
    modalSub: { fontSize: 13, color: MUTED, marginBottom: "1rem" },
    modalClose: { background: "none", border: `1px solid ${BORDER}`, color: MUTED, borderRadius: 7, padding: "7px 16px", fontFamily: B, fontSize: 13, cursor: "pointer", marginTop: "1rem", width: "100%" },
    dlBtn: { width: "100%", padding: "10px", background: V, color: WHITE, border: "none", borderRadius: 8, fontFamily: H, fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: ".5rem" },
  }

  const imgMap = {}
  week.imagenes.forEach(img => { imgMap[img.id] = img })

  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap');
        @keyframes fi{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .fi{animation:fi .3s ease}
      `}</style>

      {/* IMAGE MODAL */}
      {viewImg && (
        <div style={s.modal} onClick={() => setViewImg(null)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>{t.imgFor} {days[viewImg.dia]}</div>
            <div style={s.modalSub}>{viewImg.concepto}</div>
            {viewImg.urls && viewImg.urls.length > 0 ? (
              viewImg.urls.map((u, i) => (
                <div key={i} style={{marginBottom:12}}>
                  <img src={u} alt={`img ${i+1}`} style={{width:"100%",height:"auto",borderRadius:8,display:"block",marginBottom:6}}/>
                  <button style={s.dlBtn} onClick={()=>window.open(u, "_blank")}>↓ {t.downloadBtn} {i+1}</button>
                </div>
              ))
            ) : viewImg.url ? (
              <>
                <img src={viewImg.url} alt={viewImg.concepto} style={{width:"100%",height:"auto",borderRadius:8,display:"block",marginBottom:8}}/>
                <button style={s.dlBtn} onClick={()=>window.open(viewImg.url, "_blank")}>↓ {t.downloadBtn}</button>
              </>
            ) : (
              <ImageMockup concepto={viewImg.concepto} estilo={week.estilo_imagen} nombre={nombreProyecto || "Mi negocio"} />
            )}
            <button style={s.modalClose} onClick={() => setViewImg(null)}>Cerrar</button>
          </div>
        </div>
      )}

      <div style={s.header}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
          <button onClick={()=>navigate("/dashboard")} style={{background:"none",border:"none",color:V,fontFamily:B,fontSize:13,fontWeight:700,cursor:"pointer",padding:"4px 0",display:"flex",alignItems:"center",gap:5}}>
            <span>←</span><span>{es ? "Mis proyectos" : "My projects"}</span>
          </button>
        </div>
        <div style={s.headerRight}>
          <div style={s.brandChip}><div style={s.brandDot}/><span style={s.brandName}>{nombreProyecto || (es?"Mi negocio":"My business")}</span></div>
          <div style={s.langRow}>
            <button style={s.langBtn(es)} onClick={() => setLang("es")}>ES</button>
            <button style={s.langBtn(!es)} onClick={() => setLang("en")}>EN</button>
          </div>
        </div>
      </div>

      <div style={s.layout}>
        {/* SIDEBAR */}
        <div style={s.sidebar}>
          <div style={s.sideLabel}>{t.weeks}</div>
          {weeks.map(w => {
            const wp = published[w.id] || []
            const wc = wp.filter(Boolean).length
            return (
              <div key={w.id} style={s.weekItem(activeWeek === w.id)} onClick={() => setActiveWeek(w.id)}>
                <div style={s.weekLine(activeWeek === w.id)} />
                {w.current && <div style={s.curBadge}>{t.thisWeek}</div>}
                <div style={s.weekLabel(activeWeek === w.id)}>{w.label}</div>
                <div style={s.weekDates}>{w.dates}</div>
                <div style={s.weekProg}>
                  <div style={s.weekProgBar}><div style={s.weekProgFill((wc/7)*100, w.current)}/></div>
                  <span style={s.weekProgTxt}>{wc}/7</span>
                </div>
              </div>
            )
          })}
          <button style={s.newWeekBtn}>{t.newWeek}</button>
        </div>

        {/* MAIN */}
        <div style={s.main}>
          <div className="fi" key={activeWeek}>

            {/* WEEK HEADER */}
            <div style={s.topRow}>
              <div style={s.topLeft}>
                <div style={s.topLbl}>{t.arch}</div>
                <div style={s.weekTitle}>{week.label}</div>
                <div style={s.weekMeta}>
                  <span style={s.weekDatesTxt}>{week.dates}</span>
                  <span style={s.focusChip}>{t.focus}: {week.focus}</span>
                </div>
              </div>
              <div style={s.progBox}>
                <div style={s.progTop}>
                  <span style={s.progLbl}>{t.progress}</span>
                  <span style={s.progNum(allDone)}>{pubCount}/7</span>
                </div>
                <div style={s.progBar}><div style={s.progFill(allDone)}/></div>
              </div>
            </div>

            {/* STRATEGY */}
            <div style={s.stratBox}>
              <div style={s.stratDot}/>
              <div style={s.stratTxt}>{week.estrategia}</div>
            </div>

            {/* IMAGES SECTION */}
            <div style={s.imgSection}>
              <div style={s.imgSectionTop}>
                <div style={s.imgSectionLbl}>{t.images}</div>
                <div style={s.imgStorageBadge}>
                  <div style={s.imgStorageDot}/>
                  {generatedImgs.length} {generatedImgs.length === 1 ? t.imgCount : t.imgCountP} · {t.imgStorage}
                </div>
              </div>
              <div style={s.imgGrid}>
                {week.imagenes.map(img => (
                  <div key={img.id} style={s.imgThumb(img.generada)} onClick={() => img.generada && setViewImg(img)}>
                    {img.url ? (
                      <img src={img.url} alt={img.concepto} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:8,display:"block"}}/>
                    ) : (
                      <ImageMockup concepto={img.concepto} estilo={week.estilo_imagen} nombre={nombreProyecto || "Mi negocio"} small />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ALL DONE */}
            {allDone && <div style={s.allDoneBanner}>{t.allDone} 🎉</div>}

            {/* DAYS */}
            {week.dias.map((dia, i) => {
              const pub = weekPub[i] || false
              const exp = expandedDay === i
              const dayImg = dia.img_id ? imgMap[dia.img_id] : null
              const isVideo = dia.formato === "Reel" || dia.formato === "Video" || dia.formato === "Story" || dia.formato === "Historia"
              return (
                <div key={i} style={s.dayCard(pub, exp)}>
                  <div style={s.dayHeader(pub)} onClick={() => setExpandedDay(exp ? null : i)}>
                    <div style={s.dayLeft}>
                      <div style={s.checkbox(pub)} onClick={e => { e.stopPropagation(); togglePub(activeWeek, i) }}>
                        {pub && <span style={s.checkmark}>✓</span>}
                      </div>
                      <div>
                        <div style={s.dayName(pub)}>{days[i]}</div>
                        <div style={s.dayStatus(pub)}>{pub ? t.published : t.pending}</div>
                      </div>
                    </div>
                    <div style={s.dayRight}>
                      <div style={s.fmtBadge(dia.formato)}>{dia.formato}</div>
                      <span style={s.expandIcon(exp)}>▾</span>
                    </div>
                  </div>

                  {exp && (() => {
                    const key = `${activeWeek}-${i}`
                    const isEditing = editingDay === key
                    const ed = edits[key] || {}
                    const hookVal = ed.hook ?? dia.hook
                    const ctaVal  = ed.cta  ?? dia.cta
                    const concVal = ed.concepto ?? (dayImg?.concepto || week.img_concepto || "")
                    const editStyle = { width:"100%", border:`1.5px solid ${V_MID}`, borderRadius:8, padding:"8px 10px", fontFamily:B, fontSize:13, color:TEXT, outline:"none", resize:"vertical", lineHeight:1.6, background:V_SOFT, minHeight:60 }
                    return (
                    <div style={s.dayBody}>
                      {!pub && (
                        <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
                          {isEditing
                            ? <button style={{ padding:"5px 14px", background:V, color:WHITE, border:"none", borderRadius:7, fontFamily:B, fontSize:11, fontWeight:700, cursor:"pointer" }}
                                onClick={()=>guardarEdicion(activeWeek, i)}>
                                {es?"Guardar cambios":"Save changes"}
                              </button>
                            : <button style={{ padding:"5px 14px", background:OFF, color:V, border:`1px solid ${V_MID}`, borderRadius:7, fontFamily:B, fontSize:11, fontWeight:600, cursor:"pointer" }}
                                onClick={()=>setEditingDay(key)}>
                                {es?"Editar contenido":"Edit content"}
                              </button>
                          }
                        </div>
                      )}
                      <div style={s.dayGrid}>
                        <div style={s.dayContent}>
                          <div style={s.fieldBlock}>
                            <div style={s.miniLbl(pub)}>{t.hook}</div>
                            {isEditing
                              ? <textarea style={editStyle} value={hookVal} onChange={e=>setEdits(prev=>({...prev,[key]:{...ed,hook:e.target.value}}))}/>
                              : <div style={s.contentRow}>
                                  <div style={s.contentTxt(pub)}>{hookVal}</div>
                                  {!pub && <button style={s.copyBtn(copied===`h${activeWeek}${i}`)} onClick={() => copy(hookVal,`h${activeWeek}${i}`)}>{copied===`h${activeWeek}${i}`?t.copied:t.copy}</button>}
                                </div>
                            }
                            {isVideo && !pub && <div style={s.videoNote}>{t.videoNote}</div>}
                          </div>
                          <div style={s.divLine}/>
                          <div style={s.fieldBlock}>
                            <div style={s.miniLbl(pub)}>{t.cta}</div>
                            {isEditing
                              ? <textarea style={{...editStyle,minHeight:48}} value={ctaVal} onChange={e=>setEdits(prev=>({...prev,[key]:{...ed,cta:e.target.value}}))}/>
                              : <div style={s.contentRow}>
                                  <div style={s.ctaTxt(pub)}>{ctaVal}</div>
                                  {!pub && <button style={s.copyBtn(copied===`c${activeWeek}${i}`)} onClick={() => copy(ctaVal,`c${activeWeek}${i}`)}>{copied===`c${activeWeek}${i}`?t.copied:t.copy}</button>}
                                </div>
                            }
                          </div>
                          <div style={s.divLine}/>
                          <div style={s.fieldBlock}>
                            <div style={{ fontSize:9, fontWeight:700, letterSpacing:".1em", color:MUTED, textTransform:"uppercase", marginBottom:5, fontFamily:B }}>
                              {es?"Concepto de imagen":"Image concept"}
                            </div>
                            {isEditing
                              ? <textarea style={{...editStyle,minHeight:52}} value={concVal} onChange={e=>setEdits(prev=>({...prev,[key]:{...ed,concepto:e.target.value}}))}/>
                              : <div style={{ fontSize:12, color:MUTED, lineHeight:1.6, fontStyle:"italic", fontFamily:B, background:OFF, border:`1px solid ${BORDER}`, borderRadius:7, padding:"7px 10px" }}>{concVal || (es?"Sin concepto definido":"No concept defined")}</div>
                            }
                          </div>
                        </div>

                        {/* DAY IMAGE */}
                        <div style={s.dayImgWrap}>
                          <div style={s.dayImgLbl}>{es?"Imagen":"Image"}</div>
                          {dayImg && (dayImg.url || (dayImg.urls && dayImg.urls.length > 0)) ? (
                            <>
                              <img src={dayImg.url || dayImg.urls[0]} alt={dayImg.concepto} style={{width:"100%",height:"auto",borderRadius:8,display:"block"}}/>
                              <button style={s.dayViewBtn} onClick={() => setViewImg(dayImg)}>{t.viewBtn}</button>
                            </>
                          ) : (
                            <>
                              <div style={{ height: 72, background: OFF, border: `1.5px dashed ${BORDER}`, borderRadius: 8, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:5 }}>
                                <span style={{ fontSize:11, color:MUTED, fontFamily:B }}>{es?"Sin imagen":"No image"}</span>
                              </div>
                              <button style={s.dayGenBtn} onClick={() => navigate(`/weekly?week=${activeWeek}`)}>+ {es?"Generar imagen":"Generate image"}</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    )
                  })()}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
